// apps/web/src/features/crm/leads/list/EngagementStageBadge.tsx
//
// Read-only funnel-state chip for the v4 projection (#2247 FR-1/FR-2). Renders a
// leading colour dot + a human lead/deal-flavoured label from the
// `(phase, stage)` pair. Feature-local (like `LeadStageBadge` / `RequestStatusPill`)
// because the shared `StatusBadge` resolves labels from the fixed `common:status.*`
// catalog and cannot carry a funnel-specific label. Text label is the non-colour
// signal; no role/aria (a static chip).
import { useTranslation } from 'react-i18next';
import { EngagementPhase, EngagementStage } from '@magimp/shared';

type Tone = 'neutral' | 'info' | 'success' | 'error';

const TONE_STYLE: Record<Tone, { bg: string; fg: string }> = {
  neutral: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
  info: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  success: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-fg)' },
  error: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
};

export interface EngagementStageBadgeProps {
  phase: EngagementPhase;
  stage: EngagementStage;
  size?: 'sm' | 'md';
}

export function EngagementStageBadge({ phase, stage, size = 'md' }: EngagementStageBadgeProps) {
  const { t } = useTranslation(['leads']);
  const { token, tone } = describe(phase, stage);
  // WHY: literal `t('...')` per token — i18next's typed-key contract rejects a
  // runtime `t(dynamicKey)` (mirrors `deals/DealStageBadge`).
  const labels: Record<LabelToken, string> = {
    deal: t('leads:card.dealCode'),
    closing: t('leads:card.funnel.closing'),
    new: t('leads:card.funnel.new'),
    inWork: t('leads:card.funnel.inWork'),
    postponed: t('leads:stage.postponed'),
    nonTarget: t('leads:stage.notSuitable'),
    refusal: t('leads:stage.refusal'),
  };
  const label = labels[token];
  const h = size === 'sm' ? 20 : 22;
  const s = TONE_STYLE[tone];
  return (
    <span
      data-testid={`engagement-stage-${phase}-${stage}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: h,
        padding: `0 ${size === 'sm' ? 7 : 9}px`,
        borderRadius: 'var(--r-1)',
        fontSize: size === 'sm' ? 11 : 12,
        fontWeight: 500,
        whiteSpace: 'nowrap',
        background: s.bg,
        color: s.fg,
      }}
    >
      <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
      {label}
    </span>
  );
}

type LabelToken = 'deal' | 'closing' | 'new' | 'inWork' | 'postponed' | 'nonTarget' | 'refusal';

// Pure (phase, stage) → label-token + tone. Kept free of `t` so the label stays
// a literal `t('...')` lookup at the call site (i18next typed-key contract).
// Phase wins for CLOSING/DEAL; the LEAD stage otherwise picks token + tone.
function describe(
  phase: EngagementPhase,
  stage: EngagementStage,
): { token: LabelToken; tone: Tone } {
  if (phase === EngagementPhase.DEAL) return { token: 'deal', tone: 'success' };
  if (phase === EngagementPhase.CLOSING) return { token: 'closing', tone: 'info' };
  switch (stage) {
    case EngagementStage.NEW:
      return { token: 'new', tone: 'neutral' };
    case EngagementStage.IN_WORK:
      return { token: 'inWork', tone: 'info' };
    case EngagementStage.POSTPONED:
      return { token: 'postponed', tone: 'neutral' };
    case EngagementStage.NON_TARGET:
      return { token: 'nonTarget', tone: 'neutral' };
    case EngagementStage.REFUSED:
      return { token: 'refusal', tone: 'error' };
  }
}
